import React from 'react';
import { Lightbulb, TrendingUp, Star, Users } from 'lucide-react';

const AdvantagesSection: React.FC = () => {
  return (
    <section id="advantages" className="py-20 bg-gradient-to-b from-navy-900 to-navy-950">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Your Advantage</h2>
          <div className="w-20 h-1 bg-gold-500 mx-auto rounded-full mb-6"></div>
          <p className="text-gray-300 max-w-2xl mx-auto text-lg">
            Gain the benefits of DeFi yields without the complexity, risk, or technical knowledge typically required.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          <div className="bg-navy-800/70 backdrop-blur-sm rounded-xl p-8 border border-gray-700 transform transition-transform hover:-translate-y-1">
            <div className="text-gold-500 mb-4">
              <Lightbulb size={36} />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">Confidence without expertise</h3>
            <p className="text-gray-300">Our team audits every strategy and monitors risk around the clock, so you don't need to master smart contracts.</p>
          </div>

          <div className="bg-navy-800/70 backdrop-blur-sm rounded-xl p-8 border border-gray-700 transform transition-transform hover:-translate-y-1">
            <div className="text-gold-500 mb-4">
              <TrendingUp size={36} />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">Returns above bank rates</h3>
            <p className="text-gray-300">Target yields significantly outpace traditional deposits of comparable risk.</p>
          </div>

          <div className="bg-navy-800/70 backdrop-blur-sm rounded-xl p-8 border border-gray-700 transform transition-transform hover:-translate-y-1">
            <div className="text-gold-500 mb-4">
              <Star size={36} />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">Innovative image, prudent practice</h3>
            <p className="text-gray-300">Showcase forward-thinking asset management while safeguarding your reputation for caution.</p>
          </div>

          <div className="bg-navy-800/70 backdrop-blur-sm rounded-xl p-8 border border-gray-700 transform transition-transform hover:-translate-y-1">
            <div className="text-gold-500 mb-4">
              <Users size={36} />
            </div>
            <h3 className="text-xl font-semibold text-white mb-3">Personalized service</h3>
            <p className="text-gray-300">Whether you're a family office or an individual investor, we tailor deposit size and structure to your goals.</p>
          </div>
        </div>

        <div className="mt-16 bg-navy-800 rounded-xl p-8 max-w-5xl mx-auto border border-gray-700">
          <div className="flex flex-col md:flex-row items-center">
            <div className="md:w-2/3 mb-8 md:mb-0 md:pr-8">
              <h3 className="text-2xl font-semibold text-white mb-4">Investment Performance</h3>
              <p className="text-gray-300 mb-6">
                Our carefully selected strategy portfolio has delivered consistent returns over time, with lower volatility than traditional crypto investments.
              </p>
              <div className="bg-navy-700 rounded-lg p-6">
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Target Annual Yield</p>
                    <p className="text-gold-500 text-3xl font-bold">7-31%</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Minimum Investment</p>
                    <p className="text-white text-3xl font-bold">$50,000</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Payout Frequency</p>
                    <p className="text-white text-xl font-semibold">Quarterly</p>
                  </div>
                  <div>
                    <p className="text-gray-400 text-sm mb-2">Liquidity</p>
                    <p className="text-white text-xl font-semibold">30 days notice</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="md:w-1/3">
              <div className="bg-navy-700 rounded-lg p-6 border border-gray-600">
                <h4 className="text-lg font-semibold text-white mb-4">Historical Performance</h4>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-300 text-sm">2024 Q3</span>
                      <span className="text-green-400 text-sm">+16.32%</span>
                    </div>
                    <div className="w-full bg-navy-600 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '53%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-300 text-sm">2024 Q4</span>
                      <span className="text-green-400 text-sm">+27.35%</span>
                    </div>
                    <div className="w-full bg-navy-600 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '88%' }}></div>
                    </div>
                  </div>
                  <div>
                    <div className="flex justify-between mb-1">
                      <span className="text-gray-300 text-sm">2025 Q1</span>
                      <span className="text-green-400 text-sm">+7.12%</span>
                    </div>
                    <div className="w-full bg-navy-600 rounded-full h-2">
                      <div className="bg-green-500 h-2 rounded-full" style={{ width: '23%' }}></div>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-gray-400 mt-4">*Past performance does not guarantee future results</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AdvantagesSection;